import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import DataTable from "react-data-table-component";
import { customStyles } from "../../../assets/table-style";
import "@fortawesome/fontawesome-free/css/all.min.css";
import BASE_URL from "../../../assets/global/url";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Link } from "react-router-dom";
import { ThreeDot } from "react-loading-indicators";
import NoAccess from "../../../assets/img/NoAccess.png";
import swal from "sweetalert";
import useDecodeToken from "../../../hooks/customHook/useDecodeToken";
import DatePicker from "react-datepicker";
import { format, max } from "date-fns";
import { useServerPagination } from "../../../hooks/customHook/paginationHook/useServerPagination";
import { PaginationControls } from "../../../hooks/customHook/paginationHook/usePagination";
import { useValidPage } from "../../../hooks/customHook/paginationHook/useValidPage";
import { Button, Modal } from "react-bootstrap";
import { formatToTwoDecimal } from "../../../utils/numberFormatter";
import { useServerSort } from "../../../hooks/customHook/sortingHook/useTableSort";
import FilterStore from "../../../hooks/customHook/FilterStore";

const ModalContentPO = ({ activeTab, onBeforeView }) => {
  const navigate = useNavigate();
  const userLoggedID = useDecodeToken();

  // FILTER RETENTION keys. Same LOOVP helpers the Local/Overseas Purchase tab uses, but in their
  // own namespace (per tab) so they never mix with the tab's own saved search / page / perPage.
  const listKey = `${activeTab}_purchase_transaction_list`; // vendor list: search + page + perPage
  const detailKey = `${activeTab}_purchase_transaction_details`; // Payables Detail table: perPage

  const debounceTimer = useRef(null);
  const statusRequestId = useRef(null);
  const pageRestored = useRef(false); // save gate of the vendor list page
  const [searchText, setSearchText] = useState(() =>
    FilterStore.LOOVPgetSearch(listKey),
  );
  const [expandedData, setExpandedData] = useState({});
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [statusTabs, setStatusTabs] = useState([]);
  const [activeStatus, setActiveStatus] = useState("All");

  const getBulkGross = (pb) =>
    (Number(pb.total_amount) || 0) + (Number(pb.ovad_converted) || 0); // eto naman

  const getBulkPaid = (pb) =>
    (pb.payable_bulk_payments || []).reduce(
      (sum, p) => sum + (Number(p.amount) || 0), // para ito sa pagkuha ng total paid amount ng isang bulk payable route /fetch_local_payable_bulk_perVendor
      0,
    );

  const getVendorGross = (vendor) =>
    (vendor.payable_bulks || []).reduce(
      (sum, pb) => (pb.status === "Rejected" ? sum : sum + getBulkGross(pb)),
      0,
    );

  const getVendorPaid = (vendor) =>
    (vendor.payable_bulks || []).reduce(
      (sum, pb) => (pb.status === "Rejected" ? sum : sum + getBulkPaid(pb)),
      0,
    );

  const columns = [
    {
      name: "", // or "Details"
      button: true, // marks it as non-sortable etc.
      // data-tag lets row clicks (expandOnRowClicked) work on this blank cell
      cell: () => (
        <div
          data-tag="allowRowEvents"
          style={{ flex: 1, alignSelf: "stretch" }}
        />
      ),
    },
    {
      name: "Company Name",
      selector: (row) => row.company_name,
      sortField: "company_name",
    },
    {
      name: "Country",
      selector: (row) => row.company_country,
      sortField: "company_country",
    },
    {
      name: "Remaining Balance",
      selector: (row) => formatToTwoDecimal(row.total_remaining_balance),
      sortField: "total_remaining_balance",
    },
    {
      name: "Total Gross",
      selector: (row) => formatToTwoDecimal(getVendorGross(row)),
      sortField: "total_gross",
    },
    {
      name: "Total Paid",
      selector: (row) => formatToTwoDecimal(getVendorPaid(row)),
      sortField: "total_paid",
    },
  ];

  // "Details" (perPage) starts from the saved value instead of a fixed 10
  const pagination = useServerPagination(
    "about:blank",
    FilterStore.LOOVPgetPerPage(listKey),
  ); // dummy init
  const paginationDropdown = useServerPagination(
    "about:blank",
    FilterStore.LOOVPgetPerPage(detailKey),
  ); // dummy init

  // Server-side column sorting for the main vendor list (same hook as
  // LocalOverseasTab). No initial sort: until a header is clicked, the backend
  // default (Company Name A-Z) applies. sortField on `columns` must be a key of
  // VENDOR_LIST_SORT_FIELDS in payable_route.js.
  const vendorSort = useServerSort(pagination);

  // Server-side column sorting for the expanded Payables Detail list (same hook as
  // LocalOverseasTab). No initial sort: until a header is clicked, the backend
  // default (newest first) applies.
  const sort = useServerSort(paginationDropdown);
  const [sortState, setSortState] = useState({ field: null, order: null });

  ///PARA ITO SA FILTER RETENTION NG SEARCH / PAGE / PERPAGE (same approach as the Local/Overseas Purchase tab)
  //START
  // Search of the vendor list
  useEffect(() => {
    FilterStore.LOOVPsaveSearch(searchText, listKey);
  }, [searchText, listKey]);

  // Vendor list page. The save gate stays closed until the mount restore settles,
  // so the hook's reset-to-1 from updateParams (fetchData on mount) doesn't overwrite the saved page.
  useEffect(() => {
    if (!pageRestored.current) return;
    if (pagination.currentPage) {
      FilterStore.LOOVPsavePage(pagination.currentPage, listKey);
    }
  }, [pagination.currentPage, listKey]);

  // "Details" (perPage) of the vendor list and of the Payables Detail table.
  // The Payables Detail page is not saved: it depends on the expanded vendor / status tab, which are not retained.
  useEffect(() => {
    if (pagination.itemsPerPage) {
      FilterStore.LOOVPsavePerPage(pagination.itemsPerPage, listKey);
    }
  }, [pagination.itemsPerPage, listKey]);

  useEffect(() => {
    if (paginationDropdown.itemsPerPage) {
      FilterStore.LOOVPsavePerPage(paginationDropdown.itemsPerPage, detailKey);
    }
  }, [paginationDropdown.itemsPerPage, detailKey]);

  // Restore the last viewed page of the vendor list on mount. setTimeout(600) defers until after
  // fetchData -> updateParams -> hook's reset-to-1 has settled, then opens the save gate.
  useEffect(() => {
    const savedPage = FilterStore.LOOVPgetPage(listKey);
    const timer = setTimeout(() => {
      if (savedPage > 1) pagination.setCurrentPage(savedPage);
      pageRestored.current = true;
    }, 600);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  //END

  // Same shape as LocalOverseasTab's columns. sortField must be a key of
  // VENDOR_PAYABLE_BULK_SORT_FIELDS in payable_route.js.
  const payableColumns = [
    { name: "Transaction No.", sortField: "transaction_number" },
    { name: "Transaction Date", sortField: "payable_date" },
    { name: "Date Created", sortField: "createdAt" },
    { name: "Balance", sortField: "remaining_balance" },
    { name: "Gross", sortField: "gross" },
    { name: "Total Paid", sortField: "total_paid" },
    { name: "Status", sortField: "status" },
    { name: "Action" },
  ];

  // First click sorts ascending, clicking the active column toggles asc/desc.
  // Goes through the hook's onSort, so the params reach the backend exactly like a
  // DataTable header click does.
  const handleSort = (sortField) => {
    const order =
      sortState.field === sortField && sortState.order === "asc"
        ? "desc"
        : "asc";
    setSortState({ field: sortField, order });
    sort.sortProps.onSort({ id: sortField, sortField }, order);
  };

  const getSortIconClass = (sortField) => {
    if (sortState.field !== sortField)
      return "fa-solid fa-sort ms-1 text-muted";
    return sortState.order === "asc"
      ? "fa-solid fa-sort-up ms-1"
      : "fa-solid fa-sort-down ms-1";
  };

  const fetchData = (cutOff) => {
    if (searchText) {
      // Restored search (FILTER RETENTION): same route / params as handleSearch
      pagination.updateApiUrl(
        `${BASE_URL}/payable/fetch_customer_with_payable/search`,
      );
      pagination.updateParams({ searchText, domestic_type: activeTab });
    } else {
      pagination.updateApiUrl(
        BASE_URL + "/payable/fetch_customer_with_payable",
      );
      pagination.updateParams({ domestic_type: activeTab });
    }
  };

  const fetchStatuses = async (vendorId) => {
    statusRequestId.current = vendorId;

    try {
      const res = await axios.get(
        `${BASE_URL}/payable/fetch_local_payable_bulk_perVendor/statuses`,
        { params: { domestic_type: activeTab, vendor_id: vendorId } },
      );

      if (statusRequestId.current !== vendorId) return;

      setStatusTabs(res.data.data);
    } catch (error) {
      console.error(error);
    }
  };

  const handleTabChange = (status, vendorId) => {
    if (status === activeStatus) return;

    setActiveStatus(status);
    paginationDropdown.updateParams({
      vendor_id: vendorId,
      domestic_type: activeTab,
      status,
    });
  };

  const handleSearch = (value) => {
    setSearchText(value);
    debounceTimer.current = setTimeout(() => {
      pagination.updateApiUrl(
        `${BASE_URL}/payable/fetch_customer_with_payable/search`,
      );
      pagination.updateParams({ searchText: value, domestic_type: activeTab });
    }, 500);
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tabs = [
    {
      status: "All",
      count: statusTabs.reduce((sum, tab) => sum + tab.count, 0),
    },
    ...statusTabs,
  ];

  const handleDeletePayable = async (
    primary_id,
    purchase_date,
    transaction_id,
  ) => {
    swal({
      title: "Confirm Deletion",
      text: "Are you sure you want to delete?",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (willDelete) => {
      if (willDelete) {
        try {
          const response = await axios.delete(
            `${BASE_URL}/payable/localOverseasdeletePayable/`,
            {
              params: {
                primary_id,
                purchase_date,
                transaction_id,
                userLoggedID,
              },
            },
          );
          if (response.status === 200) {
            swal({
              title: "Purchase Payment Deleted Successfully!",
              text: "The purchase payment has been successfully deleted.",
              icon: "success",
              button: "OK",
            }).then(() => {
              paginationDropdown.refreshData();
            });
          } else if (response.status === 201) {
            swal({
              title: "Delete Prohibited!",
              text: `Date (${response.data.purchase_date}) cannot be deleted as its payable date falls within the posted cutoff period named "${response.data.cutoff_name}".`,
              icon: "warning",
              button: "OK",
            });
          } else {
            swal({
              icon: "error",
              title: "Something went wrong",
              text: "Please contact our support team for assistance.",
            });
          }
        } catch (err) {
          console.log(err);
          swal({
            icon: "error",
            title: "Error",
            text: "An error occurred while deleting the invoice.",
          });
        }
      }
    });
  };

  return (
    <>
      <div className="w-100 mt-3 container-fluid">
        {/* Search Input */}
        <div className="mb-3">
          <input
            type="text"
            className="form-control"
            placeholder="Search Company Name"
            value={searchText}
            onChange={(e) => handleSearch(e.target.value)}
          />
        </div>
        <DataTable
          customStyles={customStyles}
          columns={vendorSort.sortColumns(columns)}
          className="dataTable"
          data={pagination.data}
          {...vendorSort.sortProps}
          expandableRows
          expandableRowsHeader
          expandOnRowClicked
          onRowExpandToggled={(expanded, row) => {
            if (expanded && !expandedData[row.id]) {
              // Fetch only if not yet loaded
              paginationDropdown.updateApiUrl(
                BASE_URL + "/payable/fetch_local_payable_bulk_perVendor",
              );
              paginationDropdown.updateParams({
                vendor_id: row.id,
                domestic_type: activeTab,
                status: "All",
              });

              setActiveStatus("All");
              setStatusTabs([]);
              fetchStatuses(row.id);
              setExpandedRowId(row.id);
            } else {
              setExpandedRowId(null);
            }
          }}
          expandableRowExpanded={(row) => row.id === expandedRowId}
          expandableRowsComponent={({ data }) => {
            const payables = paginationDropdown.data;

            return (
              <div>
                <h6>Payables Detail</h6>

                {/* Status Tabs */}
                {statusTabs.length > 0 && (
                  <ul className="nav nav-tabs mb-3" role="tablist">
                    {tabs.map(({ status, count }) => (
                      <li className="nav-item" key={status} role="presentation">
                        <button
                          type="button"
                          role="tab"
                          aria-selected={activeStatus === status}
                          className={`nav-link ${activeStatus === status ? "active" : ""}`}
                          onClick={() => handleTabChange(status, data.id)}
                        >
                          {status} ({count})
                        </button>
                      </li>
                    ))}
                  </ul>
                )}

                <table
                  className="table table-bordered table-sm"
                  style={{ cursor: "pointer" }}
                >
                  <thead>
                    <tr className="text-center">
                      {payableColumns.map(({ name, sortField }) =>
                        sortField ? (
                          <th
                            key={name}
                            style={{ cursor: "pointer", userSelect: "none" }}
                            onClick={() => handleSort(sortField)}
                          >
                            {name}
                            <i className={getSortIconClass(sortField)}></i>
                          </th>
                        ) : (
                          <th key={name}>{name}</th>
                        ),
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {payables && payables.length > 0 ? (
                      payables.map((payable_data) => (
                        <tr key={payable_data.id} className="text-center ">
                          <td>{payable_data.transaction_number}</td>
                          <td>
                            {format(payable_data.payable_date, "MMM/dd/yyyy")}
                          </td>
                          <td>
                            {format(payable_data.createdAt, "MMM/dd/yyyy")}
                          </td>

                          <td>
                            {formatToTwoDecimal(payable_data.remaining_balance)}
                          </td>
                          <td>
                            {formatToTwoDecimal(getBulkGross(payable_data))}
                          </td>
                          <td>
                            {formatToTwoDecimal(getBulkPaid(payable_data))}
                          </td>
                          <td>{payable_data.status}</td>
                          {/* Action */}
                          <td>
                            {" "}
                            <div className="d-flex justify-content-center align-items-center ">
                              {" "}
                              <i
                                className="fa-solid fa-eye  text-primary me-2"
                                style={{
                                  fontSize: "1.5rem",

                                  display: "inline-block",
                                  transition: "transform 0.2s ease-in-out",
                                  cursor: "pointer",
                                  opacity: 1, // Key change: use opacity
                                }}
                                title="Transaction Details"
                                onMouseEnter={(e) =>
                                  (e.currentTarget.style.transform =
                                    "scale(1.1)")
                                }
                                onMouseLeave={(e) =>
                                  (e.currentTarget.style.transform = "scale(1)")
                                }
                                onClick={() => {
                                  onBeforeView?.();
                                  navigate(
                                    `/Purchases/view-bulk-payable/${activeTab}/${payable_data.id}?page=${paginationDropdown.currentPage}`,
                                  );
                                }}
                              ></i>
                              <i
                                className="d-none fas fa-trash"
                                style={{
                                  color: "red",
                                  fontSize: "1.5rem",

                                  textDecoration: "underline",
                                  display: "inline-block",
                                  transition: "transform 0.2s ease-in-out",
                                  cursor: "pointer",
                                  opacity: 1, // Key change: use opacity
                                }}
                                title="Delete"
                                onMouseEnter={(e) =>
                                  (e.currentTarget.style.transform =
                                    "scale(1.1)")
                                }
                                onMouseLeave={(e) =>
                                  (e.currentTarget.style.transform = "scale(1)")
                                }
                                onClick={() =>
                                  handleDeletePayable(
                                    payable_data.id,
                                    payable_data.payable_date,
                                    payable_data.transaction_number,
                                  )
                                }
                              ></i>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="8" className="text-center">
                          No payables found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>

                <PaginationControls {...paginationDropdown} />
              </div>
            );
          }}
        />

        <PaginationControls {...pagination} />
      </div>
    </>
  );
};

export default ModalContentPO;
